/**
 * Display mode (RMI-2026-710): an unattended wall screen (the bedroom Mac Mini's left monitor) must pick up new app versions by itself.
 * The app deliberately waits for a click before applying an update (RMI-2026-449: registerType 'prompt', skipWaiting false) so nobody loses work;
 * that is wrong for a screen nobody is sitting at. Display mode applies a waiting update automatically, but only when the screen is idle,
 * and at most once per cooldown, so it can never reload-loop or interrupt someone who walks up and uses it.
 *
 * Turn it on by opening the app once with ?display=1 (remembered in localStorage); ?display=0 turns it off again.
 */
export const DISPLAY_FLAG = 'tecc_display_mode';
export const LAST_UPDATE_KEY = 'tecc_display_last_update';
export const DISPLAY_IDLE_MS = 60 * 1000;          // no keyboard / mouse / touch for this long before an update may reload the page
export const DISPLAY_COOLDOWN_MS = 5 * 60 * 1000;  // never auto-update more than once in this window
export const DISPLAY_TICK_MS = 30 * 1000;

const safe = (fn, fallback) => { try { return fn(); } catch { return fallback; } };

/** Reads (and remembers) display mode. `search` is window.location.search; `storage` is localStorage-like (get/set may throw in private windows). */
export const isDisplayMode = (search, storage) => {
  const q = safe(() => new URLSearchParams(search || '').get('display'), null);
  if (q === '1') { safe(() => storage?.setItem(DISPLAY_FLAG, '1')); return true; }
  if (q === '0') { safe(() => storage?.removeItem(DISPLAY_FLAG)); return false; }
  return safe(() => storage?.getItem(DISPLAY_FLAG), null) === '1';
};

/**
 * The idle/cooldown rules as a small object with an injectable clock (so they are tested without timers).
 * tick(updateAvailable) -> 'none' | 'busy' | 'cooldown' | 'update'.  trigger() is called only on 'update'.
 */
export const createDisplayUpdater = ({ trigger, storage, now = Date.now, idleMs = DISPLAY_IDLE_MS, cooldownMs = DISPLAY_COOLDOWN_MS }) => {
  let lastInput = now();
  return {
    noteInput() { lastInput = now(); },
    tick(updateAvailable) {
      if (!updateAvailable) return 'none';
      const t = now();
      if (t - lastInput < idleMs) return 'busy';
      const last = Number(safe(() => storage?.getItem(LAST_UPDATE_KEY), 0)) || 0;
      if (last && t - last < cooldownMs) return 'cooldown';
      safe(() => storage?.setItem(LAST_UPDATE_KEY, String(t)));
      trigger();
      return 'update';
    },
  };
};
